/**
 * Session ID Utility
 * Manages browser session ID that persists across the entire browser session
 * This session ID is shared across all AB test services and user journey tracking
 */

const SESSION_STORAGE_KEY = 'ab-test-session-id';

/**
 * Gets or creates a session ID that persists for the entire browser session
 * The session ID is stored in sessionStorage and remains consistent until the browser tab/window is closed
 * @returns The session ID for the current browser session
 */
export function getSessionId(): string {
  // Handle server-side rendering or environments without sessionStorage
  if (typeof sessionStorage === 'undefined') {
    return `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Generates a new unique journey ID
 * Each journey (user flow through the application) gets its own unique ID
 * @returns A new unique journey ID
 */
export function generateJourneyId(): string {
  return `journey-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Generates a unique event ID
 * Each event gets its own unique ID without any prefix
 * @returns A new unique event ID
 */
export function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Clears the session ID from storage (primarily for testing purposes)
 * In production, the session ID is automatically cleared when the browser tab/window is closed
 */
export function clearSessionId(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
}
